import type React from "react";
import { useState } from "react";

import AppIdentifierBulkAdd from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/components/AppIdentifierBulkAdd/AppIdentifierBulkAdd";
import { translate } from "app/filters/i18nFilter";
import type { IFilterViewBaseModelProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { TextField } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/components/TextField/TextField";
import { IosIdentifierDropdown } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/ios/IosIdentifierDropdown/IosIdentifierDropdown";
import { StyledDropdownsFlexContainer } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/ios/styles";
import { StyledFilterHeading } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/styles";
import type { IosIdentifierType } from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/types";
import {
    iosIdentifierTypeToAppsIdentifiers,
    parseInputValue,
} from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/AppsIdentifiersFilter/utils";
import type { AppsIosIdentifiersDescriptionDto } from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";
import type { IAppsIosIdentifierFilter } from "app/pages/sales-intelligence/store/find-apps/types";

const regExpByIdentifierType = {
    trackIds: /^[0-9]+$/,
    bundleIds: /^[a-zA-Z]([a-zA-Z0-9]*\.)+[a-zA-Z0-9]+$/,
};

export const AppsIosIdentifiersFilter: React.FC<
    IFilterViewBaseModelProps<IAppsIosIdentifierFilter, AppsIosIdentifiersDescriptionDto>
> = ({ filter, value, onFilterUpdate }) => {
    const [identifierType, setIdentifierType] = useState<IosIdentifierType>("trackIds" as IosIdentifierType);

    const identifierOptions = filter.options.identifiers as IosIdentifierType[];

    const handleUpdate = (newValue: string[]) => {
        const updatedValue = {
            ...value,
            [identifierType]: Array.from(new Set([...(value?.[identifierType] ?? []), ...newValue])),
        };
        onFilterUpdate({ trackIds: updatedValue.trackIds ?? [], bundleIds: updatedValue.bundleIds ?? [] });
    };

    const handleTextFieldUpdate = (newValue: string) => {
        handleUpdate(parseInputValue(newValue));
    };

    const validate = (inputValue: string) => {
        const regExp = regExpByIdentifierType[identifierType];
        return parseInputValue(inputValue).every((v) => regExp.test(v));
    };

    return (
        <div>
            <StyledFilterHeading>{translate("si.find_apps.filters.identifier.ios.title")}</StyledFilterHeading>
            <StyledDropdownsFlexContainer>
                <IosIdentifierDropdown
                    value={identifierType}
                    onUpdate={setIdentifierType}
                    options={identifierOptions}
                />
                <TextField
                    onUpdate={handleTextFieldUpdate}
                    placeholder={translate("si.find_apps.filters.identifier.ios.placeholder", {
                        entity: iosIdentifierTypeToAppsIdentifiers[identifierType],
                    })}
                    label={translate("si.find_apps.filters.identifier.ios.label")}
                    validate={validate}
                />
            </StyledDropdownsFlexContainer>
            <AppIdentifierBulkAdd
                onUpdate={handleUpdate}
                appIdentifierType={iosIdentifierTypeToAppsIdentifiers[identifierType]}
            />
        </div>
    );
};
